/**
 * Upload page
 * Text Input: Alternative input method for pasting text directly
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MultiFileUpload,
  type FileValidationOptions,
} from "@/features/upload/components/MultiFileUpload";
import { TextInput, type TextInputData } from "@/features/upload/components/TextInput";
import { AuditContextForm } from "@/features/upload/components/AuditContextForm";
import {
  GroupImagesCheckbox,
  canGroupImages,
} from "@/features/upload/components/GroupImagesCheckbox";
import { CreditReminderCard } from "@/features/upload/components/CreditReminderCard";
import { ReportLanguageSelect } from "@/features/upload/components/ReportLanguageSelect";
import {
  getDefaultContextValues,
  type AuditContextFormValues,
} from "@/features/upload/audit-context-form";
import {
  getStoredDefaultMarket,
} from "@/utils/language-preference";
import { useAuth } from "@/contexts/useAuth";
import {
  DOCUMENT_UPLOAD_ALLOWED_MIME_TYPES,
  EVIDENCE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ALLOWED_MIME_TYPES,
  IMAGE_UPLOAD_MAX_SIZE_BYTES,
  validateEvidenceUpload,
} from "@/features/upload/document-upload";
import { useBilling } from "@/contexts/useBilling";
import {
  UploadGuestState,
  UploadLoadingState,
} from "@/features/upload/components/UploadAuthStates";
import { useFeatureFlag } from "@/services/feature-flags";
import UploadInputModeTabs, {
  type UploadInputMode as InputMode,
} from "@/features/upload/components/UploadInputModeTabs";
import UploadSubmitButton from "@/features/upload/components/UploadSubmitButton";
import UrlInputPanel from "@/features/upload/components/UrlInputPanel";
import UploadWhatHappensNext from "@/features/upload/components/UploadWhatHappensNext";
import { useUploadSubmit } from "@/features/upload/hooks/useUploadSubmit";
import { Page } from "@/components/layout/Page";
import { PageHeader } from "@/components/layout/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";

const UPLOAD_VALIDATION: FileValidationOptions = {
  // Used only for the HTML `accept` attribute and as a fallback for callers
  // that do not supply `validateFile`. The actual per-file validation runs
  // through `validateEvidenceUpload`, which knows about the per-kind caps
  // (25 MB documents, 100 MB images).
  allowedTypes: [
    ...DOCUMENT_UPLOAD_ALLOWED_MIME_TYPES,
    ...IMAGE_UPLOAD_ALLOWED_MIME_TYPES,
  ],
  maxFileSize: IMAGE_UPLOAD_MAX_SIZE_BYTES,
  accept: EVIDENCE_UPLOAD_ACCEPT,
};

export default function Upload() {
  const { t } = useTranslation(["upload", "common"]);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { credits } = useBilling();
  const fileUploadEnabled = useFeatureFlag("FILE_UPLOAD");
  const [inputMode, setInputMode] = useState<InputMode>("text");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [groupImages, setGroupImages] = useState(false);
  const [textData, setTextData] = useState<TextInputData | null>(null);
  const [urlValue, setUrlValue] = useState("");
  const [urlAttestation, setUrlAttestation] = useState(false);
  // Per-user default target market (hydrated from the profile into localStorage)
  // silently seeds the audit form; the user can override it per run.
  const [storedDefaultMarket] = useState(getStoredDefaultMarket);
  const [contextValues, setContextValues] = useState<AuditContextFormValues>(
    () => getDefaultContextValues(storedDefaultMarket),
  );
  // pirx-c4dlb: track Q1 (communicationPurpose) answered-state separately so
  // the submit button can be disabled at the page level. Initial value
  // matches the form's q1Answered invariant (communicationPurpose !== ""),
  // then AuditContextForm's onQ1AnsweredChange callback keeps it in sync.
  const [q1Answered, setQ1Answered] = useState<boolean>(
    () => getDefaultContextValues(storedDefaultMarket).communicationPurpose !== "",
  );
  const [error, setError] = useState<string | null>(null);
  const creditUnitChars = credits?.creditUnitChars ?? 1000;

  const urlData = urlValue.trim()
    ? { url: urlValue, attestation: urlAttestation }
    : null;

  const { isUploading, handleSubmit } = useUploadSubmit({
    inputMode,
    selectedFiles,
    textData,
    urlData,
    contextValues,
    groupImages,
    onError: setError,
    onNavigate: (path, state) => void navigate(path, { state }),
  });

  const handleInputModeChange = (mode: InputMode) => {
    setInputMode(mode);
    setError(null);
    // Clear the inputs that aren't the newly-selected mode.
    if (mode !== "file") setSelectedFiles([]);
    if (mode !== "text") setTextData(null);
    if (mode !== "url") {
      setUrlValue("");
      setUrlAttestation(false);
    }
  };

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles(files);
    // Clear the grouping flag when the new batch is no longer eligible
    // (e.g. user removes the last extra image, or drops a PDF into the
    // batch) so a stale "true" does not get sent to the backend.
    if (!canGroupImages(files)) {
      setGroupImages(false);
    }
    setError(null);
  };

  const handleFileError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleTextChange = (data: TextInputData | null) => {
    setTextData(data);
    if (data) {
      setError(null);
    }
  };

  const handleTextError = (errorMessage: string | null) => {
    setError(errorMessage);
  };

  // pirx-c4dlb: page-level submit gate. Q1 must be answered (the CSR-mode
  // declaration gate's first mandatory question) before the user can submit
  // ANY intake mode. A route-layer 422 enforcing the same rule was also
  // attempted and then reverted (it broke unrelated upload-flow
  // integration tests -- see the bug doc's Resolution section); this
  // client-side gate is therefore the only enforcement today, and it
  // prevents the silent default-to-commercial-mode failure mode the
  // tester hit in audit 6058aa89-2168-4a0e-8eca-403da691ed88.
  const canSubmit = !isUploading && q1Answered;

  if (loading) {
    return <UploadLoadingState />;
  }

  if (!user) {
    return <UploadGuestState />;
  }

  return (
    <Page max={900}>
      <PageHeader
        sub={t("upload:hero.subtitle")}
        title={t("upload:hero.title")}
      />

      <div className="space-y-8">
        {/* Input sections */}
        <div className="space-y-6">
          <h2 className="text-xl font-heading text-[var(--foreground)]">
            {t("upload:uploadDocument")}
          </h2>

          {fileUploadEnabled && (
            <>
              <UploadInputModeTabs
                inputMode={inputMode}
                isUploading={isUploading}
                onInputModeChange={handleInputModeChange}
                filePanel={(
                  <>
                    <MultiFileUpload
                      onFilesSelect={handleFileSelect}
                      onError={handleFileError}
                      disabled={isUploading}
                      validationOptions={UPLOAD_VALIDATION}
                      validateFile={validateEvidenceUpload}
                      translationNamespace="upload"
                    />
                    <GroupImagesCheckbox
                      files={selectedFiles}
                      checked={groupImages}
                      onChange={setGroupImages}
                      disabled={isUploading}
                    />
                    <CreditReminderCard data-test="file-credit-reminder">
                      {t("upload:fileUpload.reminder", { creditUnitChars })}
                    </CreditReminderCard>
                  </>
                )}
                textPanel={(
                  <TextInput
                    onTextChange={handleTextChange}
                    onError={handleTextError}
                    disabled={isUploading}
                    creditUnitChars={creditUnitChars}
                    translationNamespace="upload"
                    errorId="upload-error-text"
                  />
                )}
                urlPanel={(
                  <UrlInputPanel
                    url={urlValue}
                    attestation={urlAttestation}
                    isUploading={isUploading}
                    onUrlChange={(value) => {
                      setUrlValue(value);
                      setError(null);
                    }}
                    onAttestationChange={setUrlAttestation}
                  />
                )}
              />
            </>
          )}
          {!fileUploadEnabled && (
            <TextInput
              onTextChange={handleTextChange}
              onError={handleTextError}
              disabled={isUploading}
              creditUnitChars={creditUnitChars}
              translationNamespace="upload"
              errorId="upload-error-text"
            />
          )}
        </div>

        {/* Audit Context Form */}
        <div className="border-t border-[var(--line)] pt-6">
          <AuditContextForm
            values={contextValues}
            onChange={setContextValues}
            onQ1AnsweredChange={setQ1Answered}
            defaultMarket={storedDefaultMarket}
            disabled={isUploading}
          />
        </div>

        {/* Report Language Select */}
        <div className="border-t border-[var(--line)] pt-6">
          <ReportLanguageSelect disabled={isUploading} />
        </div>

        {/* Error box */}
        {error && (
          <div className="border-t border-[var(--line)] pt-6">
            <Alert variant="error" data-test="upload-error">
              <AlertDescription id="upload-error-text" className="whitespace-pre-line">
                {error}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Submit button */}
        <div className="border-t border-[var(--line)] pt-6">
          <UploadSubmitButton
            inputMode={inputMode}
            isUploading={isUploading}
            selectedFileCount={selectedFiles.length}
            hasTextData={Boolean(textData)}
            hasUrlData={Boolean(urlData && urlData.attestation)}
            onSubmit={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </div>

      {/* Page renders its children in normal block flow with no gap utility, so
          these two trailing siblings sat flush against the submit-button block
          (measured 0px). The card takes the page's space-y-8 rhythm to read as
          its own block; the small muted disclaimer stays on a tighter mt-6 so it
          stays attached to the card it footnotes. */}
      <UploadWhatHappensNext className="mt-8" />

      <p
        className="mt-6 text-center text-xs text-[var(--muted-foreground)]"
        data-test="upload-disclaimer"
      >
        {t("upload:disclaimer")}
      </p>
    </Page>
  );
}
